/**
 * SKYHAVEN (neo_island) realism pass, 2026-11. Owner asked for each map to look
 * more realistic.
 *
 * The island used to be a square sand slab ending in a hard 0.9 m step down to
 * the sea, and the runway was a grey box with a dashed centreline. This pass adds
 * what was missing (mapDressing does facades/roofs/streets and mapProps does
 * inland rocks/bushes, so none of that is repeated here):
 *
 *  - SHORELINE: an irregular sand apron sloping from the sand edge under the
 *    water (dry, then wet and dark at the waterline), a broken foam line, and
 *    rock clusters in the surf with headlands at two corners. All of it lies past
 *    the grounded invisible wall (|x|,|z| > 113, movement.ts groundMax), so
 *    nobody can walk onto it. It covers the old hard square edge.
 *  - PALMS: leaning coconut palms in clusters on the sand ring (crowns of
 *    alpha-cut pinnate fronds, a few dead brown fronds, coconuts), plus marram
 *    grass tufts along the dune line where the sand meets the inland ground.
 *  - AIRFIELD: runway paint (edge lines, threshold piano keys, "18"/"36"
 *    designators, aiming-point and touchdown-zone bars), tyre rubber at both
 *    touchdown zones, flush edge, threshold and approach lights, and a windsock.
 *
 * VISUAL ONLY: no collider changes. Paint and lights lie flush on the boxes
 * (< 0.26 m), the palms and grass sit on the sand top, and the shoreline lies
 * outside the walkable ring. `agent/bench-extras-neo_island.mjs` checks all of
 * this against the real shared boxes through `neoIslandParts` (pure geometry,
 * no DOM). Draw calls: at most 10, all static and built once.
 */
import * as THREE from "three";
import type { MapDef, MapBox } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { surfaceMaps, worldProjectUV } from "../mapTextures.js";

export const mapIds = ["neo_island"];

// ---- shared constants (the bench reads these too) ------------------------
/** sand slab half-size (maps.ts holedSlab 230) and its top */
export const SAND_EDGE = 115;
export const SAND_TOP = 0.2;
/** water slab top (y -1.2, t 1) */
export const WATER_TOP = -0.7;
/** CollisionWorld ground wall: bounds 115 minus the 2 m inset */
export const WALK_EDGE = 113;
const RW = { x0: 49, x1: 83, z0: -75, z1: 75, top: 0.44, cx: 66 };

function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tiny geometry builder: positions + normals (computed) + rgba colours + uv. */
class Geo {
  pos: number[] = []; col: number[] = []; uv: number[] = []; idx: number[] = [];
  v(x: number, y: number, z: number, r: number, g: number, b: number, a = 1, u = 0, w = 0): number {
    this.pos.push(x, y, z); this.col.push(r, g, b, a); this.uv.push(u, w);
    return this.pos.length / 3 - 1;
  }
  tri(a: number, b: number, c: number): void { this.idx.push(a, b, c); }
  quad(a: number, b: number, c: number, d: number): void { this.idx.push(a, b, c, a, c, d); }
  build(alpha = false): THREE.BufferGeometry | null {
    if (!this.idx.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    if (alpha) g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 4));
    else {
      const c3: number[] = [];
      for (let i = 0; i < this.col.length; i += 4) c3.push(this.col[i], this.col[i + 1], this.col[i + 2]);
      g.setAttribute("color", new THREE.Float32BufferAttribute(c3, 3));
    }
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uv, 2));
    g.setIndex(this.idx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }
}

const C = (hex: string): [number, number, number] => {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
};

// ---- the perimeter ------------------------------------------------------
interface Edge { x: number; z: number; nx: number; nz: number; s: number }
/** Counter-clockwise samples around the sand square, ~2 m apart, with quarter
 *  arcs at the corners (normals sweep, base point = the corner). */
function perimeter(): Edge[] {
  const E = SAND_EDGE, out: Edge[] = [];
  let s = 0;
  const sides: [number, number, number, number, number, number][] = [
    // start x,z  dir x,z  normal x,z
    [E, -E, 0, 1, 1, 0], [E, E, -1, 0, 0, 1], [-E, E, 0, -1, -1, 0], [-E, -E, 1, 0, 0, -1],
  ];
  for (let k = 0; k < 4; k++) {
    const [sx, sz, dx, dz, nx, nz] = sides[k];
    for (let t = 1; t < 2 * E; t += 2) { out.push({ x: sx + dx * t, z: sz + dz * t, nx, nz, s }); s += 2; }
    // corner at the end of this side: normal from (nx,nz) to the next side's
    const [, , , , mx, mz] = sides[(k + 1) % 4];
    const cx = sx + dx * 2 * E, cz = sz + dz * 2 * E;
    const a0 = Math.atan2(nz, nx);
    let a1 = Math.atan2(mz, mx);
    if (a1 < a0) a1 += Math.PI * 2;
    for (let i = 0; i <= 6; i++) {
      const a = a0 + (a1 - a0) * (i / 6);
      out.push({ x: cx, z: cz, nx: Math.cos(a), nz: Math.sin(a), s });
      s += 1.2;
    }
  }
  return out;
}

/** A seamless (periodic) 1D noise around the perimeter in [-1, 1]. */
function loopNoise(P: number, rnd: () => number, terms: [number, number][]): (s: number) => number {
  const ph = terms.map(() => rnd() * Math.PI * 2);
  let norm = 0;
  for (const [, a] of terms) norm += a;
  return (s: number) => {
    let v = 0;
    terms.forEach(([k, a], i) => { v += a * Math.sin((s / P) * Math.PI * 2 * k + ph[i]); });
    return v / norm;
  };
}

// ---- parts --------------------------------------------------------------
export interface NeoIslandParts {
  apron: THREE.BufferGeometry | null;
  foam: THREE.BufferGeometry | null;
  rock: THREE.BufferGeometry;
  rockMatrices: THREE.Matrix4[];
  trunks: THREE.BufferGeometry | null;
  fronds: THREE.BufferGeometry | null;
  grass: THREE.BufferGeometry | null;
  paint: THREE.BufferGeometry | null;
  skids: THREE.BufferGeometry | null;
  lights: THREE.BufferGeometry | null;
  sock: THREE.BufferGeometry | null;
  /** palm base points, for the bench */
  palmBases: [number, number][];
}

/** Highest box top whose XZ footprint contains (x,z), ignoring boxes that start
 *  above `below` (the bench uses the same rule). */
export function topUnder(boxes: readonly MapBox[], x: number, z: number, below = 3): number {
  let top = -Infinity;
  for (const b of boxes) {
    if (Math.abs(x - b.p[0]) > b.s[0] / 2 || Math.abs(z - b.p[2]) > b.s[2] / 2) continue;
    const bot = b.p[1] - b.s[1] / 2, t = b.p[1] + b.s[1] / 2;
    if (bot > below) continue;
    if (t > top) top = t;
  }
  return top;
}

/** Any box standing above the ground (top > 0.5) within r of (x,z)? */
function obstacleNear(boxes: readonly MapBox[], x: number, z: number, r: number, yMax = 14): boolean {
  for (const b of boxes) {
    const bot = b.p[1] - b.s[1] / 2, t = b.p[1] + b.s[1] / 2;
    if (t <= 0.5 || bot > yMax) continue;
    const dx = Math.max(0, Math.abs(x - b.p[0]) - b.s[0] / 2);
    const dz = Math.max(0, Math.abs(z - b.p[2]) - b.s[2] / 2);
    if (dx * dx + dz * dz < r * r) return true;
  }
  return false;
}

export function neoIslandParts(map: MapDef, lowSpec: boolean): NeoIslandParts {
  const rnd = rng(0x5ca7e);
  const boxes = map.boxes;
  const per = perimeter();
  const P = per[per.length - 1].s + 1.2;
  const widthN = loopNoise(P, rnd, [[3, 1], [7, 0.7], [13, 0.45], [29, 0.25], [53, 0.12]]);
  const foamN = loopNoise(P, rnd, [[41, 1], [97, 0.8], [171, 0.5]]);
  const width = (e: Edge): number => {
    // corners bulge out a little (rounded headland look)
    const corner = Math.abs(e.nx) > 0.05 && Math.abs(e.nz) > 0.05 ? 3 : 0;
    return 6.5 + 3.2 * widthN(e.s) + corner;
  };

  // ---- APRON: rows across the slope (d = distance past the sand edge) -----
  const dry = C("#d8c088"), damp = C("#b49c6c"), wet = C("#7d6b4c"), sub = C("#5d5a48");
  const apron = new Geo();
  const rows = (w: number): [number, number, [number, number, number]][] => [
    // a low storm BERM along the edge: it climbs from under the sand top over
    // the renderer's 0.6 m concrete kerb (renderer ground dressing insets it
    // at 114.4..115, 0.16 m high), so the beach reads as sand running into the
    // sea instead of a paved square. Everything above the sand top lies at
    // |x|,|z| >= 114.3 — past the 113 m grounded wall, unreachable on foot.
    [-0.72, SAND_TOP - 0.03, dry],
    [-0.64, SAND_TOP + 0.185, dry],
    [0.1, SAND_TOP + 0.2, dry],
    [0.2 * w + 0.3, 0.12, dry],
    [0.42 * w, -0.2, damp],
    [0.7 * w, -0.55, wet],
    [0.88 * w, -0.72, wet],
    [w, -0.9, sub],
    [w + 6, -2.4, sub],
  ];
  const NR = 9;
  for (const e of per) {
    const w = width(e);
    for (const [d, y, c] of rows(w)) {
      const x = e.x + e.nx * d, z = e.z + e.nz * d;
      apron.v(x, y, z, c[0], c[1], c[2], 1, x / 6, z / 6);
    }
  }
  const N = per.length;
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N;
    for (let r = 0; r + 1 < NR; r++) apron.quad(i * NR + r, j * NR + r, j * NR + r + 1, i * NR + r + 1);
  }

  // ---- FOAM: a broken white line where the slope meets the water ----------
  const foam = new Geo();
  for (const e of per) {
    const w = width(e);
    const a = Math.min(1, Math.max(0, 0.45 + 0.75 * foamN(e.s))) * 0.9;
    const rowsF: [number, number, number][] = [[0.74 * w, -0.56, 0], [0.86 * w, -0.655, a], [w + 0.4, -0.675, a * 0.55], [w + 3, -0.685, 0]];
    for (const [d, y, al] of rowsF) foam.v(e.x + e.nx * d, y, e.z + e.nz * d, 1, 1, 1, al);
  }
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N;
    for (let r = 0; r < 3; r++) foam.quad(i * 4 + r, j * 4 + r, j * 4 + r + 1, i * 4 + r + 1);
  }

  // ---- ROCKS: one deformed icosahedron, instanced --------------------------
  const rock = new THREE.IcosahedronGeometry(1, lowSpec ? 1 : 2);
  {
    const p = rock.getAttribute("position");
    const rr = rng(77);
    const bumps = [0, 1, 2, 3, 4].map(() => new THREE.Vector3(rr() - 0.5, rr() - 0.5, rr() - 0.5).normalize());
    const v = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i).normalize();
      let k = 1;
      for (const b of bumps) k += 0.12 * Math.max(0, v.dot(b)) ** 3;
      k += 0.05 * Math.sin(v.x * 9.1 + v.y * 4.3) * Math.sin(v.z * 7.7);
      // a flattened base and a squarer top read as weathered stone
      const y = v.y < -0.35 ? -0.35 - (v.y + 0.35) * 0.3 : v.y;
      p.setXYZ(i, v.x * k, y * k * 0.8, v.z * k);
    }
    rock.deleteAttribute("uv");
    rock.setAttribute("uv", new THREE.Float32BufferAttribute(new Float32Array(p.count * 2), 2));
    rock.computeVertexNormals();
    worldProjectUV(rock, 0.9);
    rock.computeBoundingBox();
  }
  const rockMatrices: THREE.Matrix4[] = [];
  {
    const rb = rock.boundingBox!;
    const q = new THREE.Quaternion(), sc = new THREE.Vector3(), pos = new THREE.Vector3(), eu = new THREE.Euler();
    const corner = new THREE.Box3();
    const tryRock = (x: number, z: number, r: number, y: number): void => {
      sc.set(r * (0.8 + rnd() * 0.6), r * (0.6 + rnd() * 0.5), r * (0.8 + rnd() * 0.6));
      eu.set((rnd() - 0.5) * 0.4, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.4);
      q.setFromEuler(eu);
      pos.set(x, y, z);
      const m = new THREE.Matrix4().compose(pos, q, sc);
      corner.copy(rb).applyMatrix4(m);
      // contract: nothing above the sand top inside the walkable square
      const insideWalk = corner.min.x < WALK_EDGE && corner.max.x > -WALK_EDGE && corner.min.z < WALK_EDGE && corner.max.z > -WALK_EDGE;
      if (insideWalk && corner.max.y > SAND_TOP) return;
      rockMatrices.push(m);
    };
    // scattered surf rocks
    const nScatter = lowSpec ? 26 : 60;
    for (let i = 0; i < nScatter; i++) {
      const e = per[Math.floor(rnd() * N)];
      const w = width(e);
      const d = w * (0.75 + rnd() * 0.25) + rnd() * 4;
      const along = (rnd() - 0.5) * 1.8;
      const x = e.x + e.nx * d - e.nz * along, z = e.z + e.nz * d + e.nx * along;
      const r = 0.4 + rnd() * rnd() * 1.6;
      tryRock(x, z, r, -0.75 - r * 0.15 - (d - w) * 0.18);
    }
    // two rocky headlands (NE and SW corners) — big stones heaped in the surf
    for (const [cx, cz] of [[1, 1], [-1, -1]] as const) {
      const n = lowSpec ? 8 : 16;
      for (let i = 0; i < n; i++) {
        const a = Math.atan2(cz, cx) + (rnd() - 0.5) * 1.6;
        const d = 4.5 + rnd() * 6;
        const x = cx * SAND_EDGE + Math.cos(a) * d, z = cz * SAND_EDGE + Math.sin(a) * d;
        const r = 1.0 + rnd() * 2.2;
        tryRock(x, z, r, -0.6 - r * 0.1 + (rnd() - 0.4) * 0.5);
      }
    }
  }

  // ---- PALMS ----------------------------------------------------------------
  const avoid: [number, number, number][] = [];
  for (const s of map.spawns) avoid.push([s.p[0], s.p[2], 5]);
  for (const p of map.pickups ?? []) avoid.push([p.p[0], p.p[2], 4]);
  for (const j of map.jumpPads ?? []) avoid.push([j.p[0], j.p[2], 5]);
  for (const v of map.vehicles ?? []) avoid.push([v.p[0], v.p[2], 8]);
  for (const [hx, hz] of [[104, 0], [-104, 0], [0, 104], [0, -104]]) avoid.push([hx, hz, 12]);
  const clearOf = (x: number, z: number, pad: number): boolean =>
    avoid.every(([ax, az, r]) => Math.hypot(x - ax, z - az) > r + pad);
  const onSand = (x: number, z: number, lo: number, hi: number): boolean => {
    const m = Math.max(Math.abs(x), Math.abs(z));
    return m > lo && m < hi && Math.abs(topUnder(boxes, x, z) - SAND_TOP) < 0.01;
  };

  const trunks = new Geo(), fronds = new Geo();
  const palmBases: [number, number][] = [];
  const barkC = C("#8f8170"), barkD = C("#6c6052"), nutC = C("#4d3c22"), shaftC = C("#6f7b3c");
  const leafA = C("#4f7a2c"), leafB = C("#6e8d34"), deadC = C("#8a6a3c");
  const seg = lowSpec ? 10 : 20, rad = lowSpec ? 5 : 7;
  const palm = (bx: number, bz: number, ox: number, oz: number): void => {
    const H = 6.5 + rnd() * 4.5;
    // lean toward the sea, with a little scatter
    const la = Math.atan2(oz, ox) + (rnd() - 0.5) * 1.1;
    const lean = 0.12 + rnd() * 0.22;
    const lx = Math.cos(la), lz = Math.sin(la);
    const at = (t: number): [number, number, number] => {
      const off = H * lean * (0.7 * t * t + 0.3 * t);
      return [bx + lx * off, SAND_TOP - 0.12 + H * t, bz + lz * off];
    };
    // trunk tube
    const base = trunks.pos.length / 3;
    for (let i = 0; i <= seg; i++) {
      const t = i / seg;
      const [x, y, z] = at(t);
      let r = 0.17 + 0.07 * (1 - t) + 0.12 * Math.max(0, 1 - t * 9); // root flare
      if (!lowSpec && i % 2 === 1 && t < 0.97) r *= 0.92; // leaf-scar rings
      const shade = 0.85 + 0.15 * Math.sin(t * 19);
      const c = i % 2 ? barkD : barkC;
      for (let k = 0; k <= rad; k++) {
        const a = (k / rad) * Math.PI * 2;
        trunks.v(x + Math.cos(a) * r, y, z + Math.sin(a) * r, c[0] * shade, c[1] * shade, c[2] * shade, 1, (k / rad) * 2, y / 0.5);
      }
    }
    for (let i = 0; i < seg; i++) for (let k = 0; k < rad; k++) {
      const a = base + i * (rad + 1) + k, b = a + rad + 1;
      trunks.quad(a, b, b + 1, a + 1);
    }
    const [tx, ty, tz] = at(1);
    // crown shaft knob + coconuts (low icosahedra, merged into the trunk mesh)
    const blob = (x: number, y: number, z: number, r: number, c: [number, number, number], sy = 1): void => {
      const ico = new THREE.IcosahedronGeometry(r, 0);
      const p = ico.getAttribute("position");
      const b0 = trunks.pos.length / 3;
      for (let i = 0; i < p.count; i++) trunks.v(x + p.getX(i), y + p.getY(i) * sy, z + p.getZ(i), c[0], c[1], c[2], 1, p.getX(i), p.getY(i));
      for (let i = 0; i < p.count; i += 3) trunks.tri(b0 + i, b0 + i + 1, b0 + i + 2);
      ico.dispose();
    };
    blob(tx, ty + 0.1, tz, 0.3, shaftC, 1.4);
    const nNuts = 2 + Math.floor(rnd() * 4);
    for (let i = 0; i < nNuts; i++) {
      const a = rnd() * Math.PI * 2;
      blob(tx + Math.cos(a) * 0.26, ty - 0.12 - rnd() * 0.15, tz + Math.sin(a) * 0.26, 0.13, nutC);
    }
    // fronds
    const nF = (lowSpec ? 7 : 10) + Math.floor(rnd() * 3);
    const fs = lowSpec ? 6 : 9;
    for (let f = 0; f < nF; f++) {
      const dead = f >= nF - (rnd() < 0.6 ? 1 : 0) - (rnd() < 0.3 ? 1 : 0);
      const az = (f / nF) * Math.PI * 2 + rnd() * 0.5;
      const L = (dead ? 2.6 : 3.2) + rnd() * 1.4;
      const elev = dead ? -0.9 - rnd() * 0.4 : 0.55 - rnd() * 0.75;
      const droop = dead ? 0.15 : 0.55 + rnd() * 0.35;
      const Wm = (dead ? 0.55 : 0.85) + rnd() * 0.25;
      const dx = Math.cos(az), dz = Math.sin(az);
      const sx = -dz, sz = dx; // side axis
      const col = dead ? deadC : (rnd() < 0.5 ? leafA : leafB);
      const fb = fronds.pos.length / 3;
      for (let i = 0; i <= fs; i++) {
        const u = i / fs;
        const h = L * u;
        const x = tx + dx * h * Math.cos(elev * 0.6), z = tz + dz * h * Math.cos(elev * 0.6);
        const y = ty + 0.15 + L * (Math.sin(elev) * u - droop * u * u);
        const wv = Wm * Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.08)), 0.7) * (1 - 0.35 * u);
        const fold = wv * 0.35; // V-shaped cross-section: leaflets rise from the rib
        const tip = 1 - 0.25 * u;
        fronds.v(x - sx * wv, y + fold, z - sz * wv, col[0] * tip, col[1] * tip, col[2] * tip, 1, u, 0);
        fronds.v(x, y, z, col[0] * 0.8, col[1] * 0.8, col[2] * 0.8, 1, u, 0.5);
        fronds.v(x + sx * wv, y + fold, z + sz * wv, col[0] * tip, col[1] * tip, col[2] * tip, 1, u, 1);
      }
      for (let i = 0; i < fs; i++) {
        const a = fb + i * 3, b = a + 3;
        fronds.quad(a, b, b + 1, a + 1);
        fronds.quad(a + 1, b + 1, b + 2, a + 2);
      }
    }
    palmBases.push([bx, bz]);
  };
  {
    const clusters = lowSpec ? 10 : 18;
    let placed = 0;
    for (let tries = 0; placed < clusters && tries < 400; tries++) {
      const e = per[Math.floor(rnd() * N)];
      if (Math.abs(e.nx) > 0.05 && Math.abs(e.nz) > 0.05) continue;
      const inset = 4 + rnd() * 12;
      const cx = e.x - e.nx * inset, cz = e.z - e.nz * inset;
      if (!onSand(cx, cz, 95, 111) || !clearOf(cx, cz, 2) || obstacleNear(boxes, cx, cz, 7)) continue;
      if (palmBases.some(([px, pz]) => Math.hypot(px - cx, pz - cz) < 14)) continue;
      const n = 1 + Math.floor(rnd() * (lowSpec ? 2 : 4));
      let got = 0;
      for (let k = 0; k < n * 3 && got < n; k++) {
        const x = cx + (rnd() - 0.5) * 7, z = cz + (rnd() - 0.5) * 7;
        if (!onSand(x, z, 95, 111) || !clearOf(x, z, 0) || obstacleNear(boxes, x, z, 6)) continue;
        if (palmBases.some(([px, pz]) => Math.hypot(px - x, pz - z) < 2.2)) continue;
        palm(x, z, e.nx, e.nz);
        got++;
      }
      if (got) placed++;
    }
  }

  // ---- MARRAM GRASS along the dune line ----------------------------------
  const grass = new Geo();
  {
    const n = lowSpec ? 140 : 380;
    const g1 = C("#9aa55c"), g2 = C("#c2b97a"), g3 = C("#7d8d48");
    let made = 0;
    for (let tries = 0; made < n && tries < n * 6; tries++) {
      const e = per[Math.floor(rnd() * N)];
      // hug the inland edge (93) or palm bases
      let x: number, z: number;
      if (palmBases.length && rnd() < 0.3) {
        const [px, pz] = palmBases[Math.floor(rnd() * palmBases.length)];
        const a = rnd() * Math.PI * 2, d = 0.6 + rnd() * 2.5;
        x = px + Math.cos(a) * d; z = pz + Math.sin(a) * d;
      } else {
        const inset = SAND_EDGE - 93.6 - rnd() * rnd() * 7;
        x = e.x - e.nx * inset + (rnd() - 0.5) * 2; z = e.z - e.nz * inset + (rnd() - 0.5) * 2;
      }
      if (!onSand(x, z, 93.3, 111) || !clearOf(x, z, -1) || obstacleNear(boxes, x, z, 1.2)) continue;
      made++;
      const blades = lowSpec ? 6 : 9;
      const hgt = 0.35 + rnd() * 0.45;
      for (let b = 0; b < blades; b++) {
        const a = rnd() * Math.PI * 2, tilt = 0.15 + rnd() * 0.5;
        const h = hgt * (0.6 + rnd() * 0.6);
        const w = 0.025 + rnd() * 0.02;
        const px = Math.cos(a + Math.PI / 2) * w, pz = Math.sin(a + Math.PI / 2) * w;
        const tx = x + Math.cos(a) * h * tilt, tz = z + Math.sin(a) * h * tilt;
        const c = rnd() < 0.3 ? g2 : rnd() < 0.5 ? g3 : g1;
        const base = SAND_TOP - 0.02;
        const i0 = grass.v(x - px, base, z - pz, c[0] * 0.7, c[1] * 0.7, c[2] * 0.7);
        const i1 = grass.v(x + px, base, z + pz, c[0] * 0.7, c[1] * 0.7, c[2] * 0.7);
        const i2 = grass.v(tx, base + h, tz, c[0], c[1], c[2]);
        grass.tri(i0, i1, i2);
      }
    }
  }

  // ---- RUNWAY PAINT -----------------------------------------------------------
  const paint = new Geo();
  const PY = RW.top + 0.006;
  const white = C("#e9e7df");
  const rect = (xa: number, za: number, xb: number, zb: number, y = PY): void => {
    const x0 = Math.min(xa, xb), x1 = Math.max(xa, xb), z0 = Math.min(za, zb), z1 = Math.max(za, zb);
    const k = 0.9 + rnd() * 0.1; // worn paint: each mark a slightly different white
    const c = [white[0] * k, white[1] * k, white[2] * k];
    const a = paint.v(x0, y, z0, c[0], c[1], c[2]), b = paint.v(x1, y, z0, c[0], c[1], c[2]);
    const cc = paint.v(x1, y, z1, c[0], c[1], c[2]), d = paint.v(x0, y, z1, c[0], c[1], c[2]);
    paint.quad(a, d, cc, b); // CCW from above → +y normal
  };
  // side stripes
  // (in 6 m runs, so the stripe stops where the terminal overhangs the runway)
  for (const [xa, xb] of [[RW.x0 + 0.9, RW.x0 + 1.8], [RW.x1 - 1.8, RW.x1 - 0.9]]) {
    for (let z = RW.z0 + 1.2; z < RW.z1 - 1.2 - 1e-6; z += 6) {
      const z2 = Math.min(z + 6, RW.z1 - 1.2);
      if (obstacleNear(boxes, (xa + xb) / 2, (z + z2) / 2, Math.max(xb - xa, z2 - z) / 2 + 0.3, 3)) continue;
      rect(xa, z, xb, z2);
    }
  }
  // digit strokes, 7-segment style, glyph cell 3.2 x 6 (gx right, gy up)
  const SEG: Record<string, number[]> = { "1": [1, 2], "3": [0, 1, 2, 3, 6], "6": [0, 2, 3, 4, 5, 6], "8": [0, 1, 2, 3, 4, 5, 6] };
  const segRect = (sg: number): [number, number, number, number] => {
    const W = 3.2, Hh = 6, t = 0.75;
    switch (sg) {
      case 0: return [0, Hh - t, W, Hh];           // top
      case 1: return [W - t, Hh / 2, W, Hh];       // upper right
      case 2: return [W - t, 0, W, Hh / 2];        // lower right
      case 3: return [0, 0, W, t];                 // bottom
      case 4: return [0, 0, t, Hh / 2];            // lower left
      case 5: return [0, Hh / 2, t, Hh];           // upper left
      default: return [0, Hh / 2 - t / 2, W, Hh / 2 + t / 2]; // middle
    }
  };
  for (const end of [1, -1]) {
    const zE = end * RW.z1; // threshold edge
    // threshold bar + piano keys
    rect(RW.x0 + 1.8, zE - end * 2.2, RW.x1 - 1.8, zE - end * 1.2);
    for (const side of [-1, 1]) for (let i = 0; i < 6; i++) {
      const xc = RW.cx + side * (2.6 + i * 2.1);
      rect(xc - 0.6, zE - end * 13, xc + 0.6, zE - end * 3);
    }
    // designator: "36" at +z (landing toward -z), "18" at -z
    const txt = end > 0 ? "36" : "18";
    const zBase = zE - end * 17; // glyph bottom edge (nearest the threshold)
    for (let ci = 0; ci < 2; ci++) {
      const gx0 = ci === 0 ? -4 : 0.8;
      for (const sg of SEG[txt[ci]]) {
        const [a, b, c, d] = segRect(sg);
        // viewer looking toward -end·z: right = +x at the +z end, -x at the -z end
        const X = (gx: number): number => RW.cx + end * (gx0 + gx);
        const Z = (gy: number): number => zBase - end * gy;
        rect(Math.min(X(a), X(c)), Math.min(Z(b), Z(d)), Math.max(X(a), X(c)), Math.max(Z(b), Z(d)));
      }
    }
    // aiming point blocks
    for (const side of [-1, 1]) {
      const xc = RW.cx + side * 6.5;
      rect(xc - 1.1, Math.min(zE - end * 32, zE - end * 41), xc + 1.1, Math.max(zE - end * 32, zE - end * 41));
    }
    // touchdown-zone bars: 3, then 2
    for (const [zOff, nb] of [[48, 3], [58, 2]] as const) {
      for (const side of [-1, 1]) for (let i = 0; i < nb; i++) {
        const xc = RW.cx + side * (3.4 + i * 1.3);
        rect(xc - 0.4, Math.min(zE - end * zOff, zE - end * (zOff + 6)), xc + 0.4, Math.max(zE - end * zOff, zE - end * (zOff + 6)));
      }
    }
  }

  // ---- TYRE RUBBER at both touchdown zones (transparent, vertex alpha) ------
  const skids = new Geo();
  if (!lowSpec) {
    for (const end of [1, -1]) {
      for (let i = 0; i < 26; i++) {
        const gear = rnd() < 0.5 ? -1 : 1;
        const x0 = RW.cx + gear * (2.2 + rnd() * 2.6) + (rnd() - 0.5) * 1.5;
        const zs = end * (RW.z1 - 24 - rnd() * 30);
        const len = 5 + rnd() * 16;
        const w = 0.25 + rnd() * 0.3;
        const drift = (rnd() - 0.5) * 0.8;
        const al = 0.2 + rnd() * 0.35;
        const n = 6, b0 = skids.pos.length / 3;
        for (let k = 0; k <= n; k++) {
          const t = k / n;
          const x = x0 + drift * t, z = zs - end * len * t;
          const a = al * Math.sin(Math.PI * t) ** 0.5;
          skids.v(x - w / 2, PY + 0.004, z, 0.07, 0.07, 0.075, a);
          skids.v(x + w / 2, PY + 0.004, z, 0.07, 0.07, 0.075, a);
        }
        for (let k = 0; k < n; k++) {
          const a = b0 + k * 2, b = a + 2;
          if (end > 0) skids.quad(a, a + 1, b + 1, b); else skids.quad(a, b, b + 1, a + 1);
        }
      }
    }
  }

  // ---- LIGHTS: flush lenses (MeshBasic, unlit) ----------------------------
  const lights = new Geo();
  const lens = (x: number, z: number, c: [number, number, number]): void => {
    if (obstacleNear(boxes, x, z, 0.4, 3)) return; // e.g. under the terminal's overhang
    const top = topUnder(boxes, x, z);
    const r = 0.14, h = 0.09, n = 6;
    const b0 = lights.pos.length / 3;
    lights.v(x, top + h, z, c[0], c[1], c[2]);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      lights.v(x + Math.cos(a) * r, top + h * 0.5, z + Math.sin(a) * r, c[0], c[1], c[2]);
      lights.v(x + Math.cos(a) * r, top - 0.01, z + Math.sin(a) * r, c[0], c[1], c[2]);
    }
    for (let k = 0; k < n; k++) {
      const a = b0 + 1 + k * 2, b = b0 + 1 + ((k + 1) % n) * 2;
      lights.tri(b0, b, a);
      lights.quad(a, b, b + 1, a + 1);
    }
  };
  const Lw = C("#fff6dc"), Ly = C("#ffc94a"), Lg = C("#43ff7a"), Lr = C("#ff3b2e");
  for (let z = RW.z0 + 5; z <= RW.z1 - 5; z += 10) {
    const caution = Math.abs(z) > RW.z1 - 22;
    for (const x of [RW.x0 + 0.45, RW.x1 - 0.45]) lens(x, z, caution ? Ly : Lw);
  }
  for (const end of [1, -1]) {
    for (let x = RW.x0 + 1.5; x <= RW.x1 - 1.5; x += 2.6) {
      lens(x, end * (RW.z1 - 0.5), Lg);
      lens(x + 1.3, end * (RW.z1 - 0.5), Lr);
    }
  }

  // ---- WINDSOCK beside the runway -------------------------------------------
  const sock = new Geo();
  {
    const wx = 90, wz = 16;
    const gTop = topUnder(boxes, wx, wz);
    const grey = C("#8d9399"), orange = C("#ff6a1a"), wh = C("#f2f2ee"), conc = C("#9c9a92");
    // concrete pad (flush) — a 2 cm slab
    {
      const y = gTop + 0.02;
      const a = sock.v(wx - 0.8, y, wz - 0.8, ...conc), b = sock.v(wx + 0.8, y, wz - 0.8, ...conc);
      const c = sock.v(wx + 0.8, y, wz + 0.8, ...conc), d = sock.v(wx - 0.8, y, wz + 0.8, ...conc);
      sock.quad(a, d, c, b);
    }
    const tube = (pts: [number, number, number][], radii: number[], cols: [number, number, number][], n: number): void => {
      const b0 = sock.pos.length / 3;
      for (let i = 0; i < pts.length; i++) {
        const [x, y, z] = pts[i];
        // axis direction → build a ring perpendicular to it
        const [x2, y2, z2] = pts[Math.min(pts.length - 1, i + 1)];
        const [x1, y1, z1] = pts[Math.max(0, i - 1)];
        const ax = new THREE.Vector3(x2 - x1, y2 - y1, z2 - z1).normalize();
        const u = Math.abs(ax.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        const s1 = new THREE.Vector3().crossVectors(ax, u).normalize();
        const s2 = new THREE.Vector3().crossVectors(ax, s1).normalize();
        const c = cols[i];
        for (let k = 0; k <= n; k++) {
          const a = (k / n) * Math.PI * 2;
          const ox = (Math.cos(a) * s1.x + Math.sin(a) * s2.x) * radii[i];
          const oy = (Math.cos(a) * s1.y + Math.sin(a) * s2.y) * radii[i];
          const oz = (Math.cos(a) * s1.z + Math.sin(a) * s2.z) * radii[i];
          sock.v(x + ox, y + oy, z + oz, c[0], c[1], c[2]);
        }
      }
      for (let i = 0; i + 1 < pts.length; i++) for (let k = 0; k < n; k++) {
        const a = b0 + i * (n + 1) + k, b = a + n + 1;
        sock.quad(a, a + 1, b + 1, b);
      }
    };
    const H = 6.2;
    tube([[wx, gTop, wz], [wx, gTop + H, wz]], [0.075, 0.05], [grey, grey], 6);
    // the sock: frustum streaming downwind (toward -x / +z), drooping a little
    const dir = new THREE.Vector3(-0.8, -0.22, 0.55).normalize();
    const pts: [number, number, number][] = [], radii: number[] = [], cols: [number, number, number][] = [];
    const L = 3.0, n = 10;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const sag = 0.25 * t * t;
      pts.push([wx + dir.x * L * t, gTop + H - 0.3 + dir.y * L * t - sag, wz + dir.z * L * t]);
      radii.push(0.42 - 0.24 * t);
      cols.push(Math.floor(t * 5 - 1e-6) % 2 === 0 ? orange : wh);
    }
    tube(pts, radii, cols, 10);
    // pivot arm
    tube([[wx, gTop + H - 0.3, wz], [wx + dir.x * 0.1, gTop + H - 0.3, wz + dir.z * 0.1]], [0.04, 0.04], [grey, grey], 5);
  }

  return {
    apron: apron.build(), foam: foam.build(true), rock, rockMatrices,
    trunks: trunks.build(), fronds: fronds.build(), grass: grass.build(),
    paint: paint.build(), skids: skids.build(true), lights: lights.build(), sock: sock.build(),
    palmBases,
  };
}

// ---- materials (browser only) ---------------------------------------------
let leafTex: THREE.CanvasTexture | null = null;
/** A pinnate palm frond on canvas: u along the frond (0 = crown), v across it
 *  (0.5 = the rib). Leaflets sweep out toward the tip; alpha cuts the gaps. */
function frondTexture(): THREE.CanvasTexture | null {
  if (leafTex) return leafTex;
  if (typeof document === "undefined") return null;
  const W = 512, H = 128;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d");
  if (!g) return null;
  g.clearRect(0, 0, W, H);
  const r = rng(4242);
  g.lineCap = "round";
  for (let i = 0; i < 70; i++) {
    const u = 0.03 + (i / 70) * 0.95;
    const x = u * W;
    for (const side of [-1, 1]) {
      const len = (H / 2) * (0.9 + r() * 0.15);
      const sweep = 26 + r() * 14;
      const l = 150 + Math.floor(r() * 60);
      g.strokeStyle = `rgb(${l},${l + 20},${l - 40})`;
      g.lineWidth = 3.2 + r() * 1.6;
      g.beginPath();
      g.moveTo(x, H / 2);
      g.quadraticCurveTo(x + sweep * 0.4, H / 2 + side * len * 0.5, x + sweep, H / 2 + side * len * 0.98);
      g.stroke();
    }
  }
  g.strokeStyle = "rgb(200,200,150)";
  g.lineWidth = 4;
  g.beginPath(); g.moveTo(0, H / 2); g.lineTo(W, H / 2); g.stroke();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.userData.shared = true;
  leafTex = t;
  return t;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const parts = neoIslandParts(ctx.map, ctx.lowSpec);
  const g = new THREE.Group();
  g.name = "extras:neo_island";
  const add = (geo: THREE.BufferGeometry | null, mat: THREE.Material, cast: boolean, receive: boolean, name: string): void => {
    if (!geo) return;
    const m = new THREE.Mesh(geo, mat);
    m.name = name;
    m.castShadow = cast && ctx.shadows;
    m.receiveShadow = receive;
    m.matrixAutoUpdate = false;
    g.add(m);
  };

  // shoreline sand: the same photo-sand family the sand slab upgrades to
  const sandMat = new THREE.MeshStandardMaterial(surfaceMaps("terrain", { vertexColors: true, roughness: 1, metalness: 0 }, 0.8));
  sandMat.userData.npPbrFamily = "sand";
  add(parts.apron, sandMat, false, true, "shoreApron");
  const foamMat = new THREE.MeshStandardMaterial({
    color: "#ffffff", vertexColors: true, transparent: true, depthWrite: false, roughness: 0.85,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  foamMat.userData.npNoPbr = true;
  add(parts.foam, foamMat, false, false, "shoreFoam");

  const rockMat = new THREE.MeshStandardMaterial(surfaceMaps("boulder", { color: "#b3aa9a", roughness: 0.95, metalness: 0 }, 1.1));
  if (parts.rockMatrices.length) {
    const im = new THREE.InstancedMesh(parts.rock, rockMat, parts.rockMatrices.length);
    parts.rockMatrices.forEach((m, i) => im.setMatrixAt(i, m));
    im.instanceMatrix.needsUpdate = true;
    im.computeBoundingSphere();
    im.castShadow = ctx.shadows;
    im.receiveShadow = true;
    im.name = "shoreRocks";
    g.add(im);
  }

  const barkMat = new THREE.MeshStandardMaterial(surfaceMaps("bark", { vertexColors: true, roughness: 0.95, metalness: 0 }, 1));
  add(parts.trunks, barkMat, true, true, "palmTrunks");
  const frondMat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.75, metalness: 0, side: THREE.DoubleSide,
    map: frondTexture(), alphaTest: 0.45,
  });
  frondMat.userData.npNoPbr = true;
  add(parts.fronds, frondMat, true, true, "palmFronds");
  const grassMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, side: THREE.DoubleSide });
  add(parts.grass, grassMat, false, true, "marramGrass");

  const paintMat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.7, metalness: 0,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  add(parts.paint, paintMat, false, true, "runwayPaint");
  const skidMat = new THREE.MeshStandardMaterial({
    vertexColors: true, transparent: true, depthWrite: false, roughness: 0.6, metalness: 0, side: THREE.DoubleSide,
    polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
  });
  add(parts.skids, skidMat, false, true, "runwayRubber");
  const lightMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  add(parts.lights, lightMat, false, false, "runwayLights");
  const sockMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.1, side: THREE.DoubleSide });
  add(parts.sock, sockMat, true, true, "windsock");

  if (!parts.rockMatrices.length) parts.rock.dispose();
  return g;
}
